import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import type { FieldTypePillProps } from "@smartsuite/react-ui/lib";
import {
  DropdownPanel,
  MenuOption,
  MenuOptionWrapper,
  PickerInputField,
} from "@smartsuite/react-ui/lib";
import type { PillPickerEditorCommands } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import * as R from "remeda";
import { useFileUpload } from "../../../../../../../../clients";
import type { SharedFileInput } from "../../../../../../../../clients/filestack/files";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { PickerStyleWrapper } from "../../../../../../../../components/PickerStyleWrapper";
import { useTheme } from "../../../../../../../../stores";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { useConverters } from "../../../../../../../../utils/conditions-coverter.hook";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import {
  FieldMissingError,
  ErrorMessage,
} from "../../../../../../Sections/ErrorSection";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import {
  convertActionsToDynamicSections,
  convertLoopingSourceOptionsToDynamicSections,
  convertTriggerToDynamicSection,
  isSupportedOutputForFile,
} from "../../../../utils";
import { ActionOptions } from "../../containers/ActionOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { OperatorDescriptionKinds } from "../../containers/UpdateValueDropdown/constanst";
import { calculateConfigValue, ifLoopingSourceIsActionFind } from "../utils";
import { Root } from "./style";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { DateTab } from "../DateEditor";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";

export const FileEditor: React.FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  integrations: IntegrationDescription[];
  description: InputDescription;
  onClickTrash?: () => void;
  action?: ActionCandidate;
  actions: ActionCandidate[];
}> = ({
  input,
  onChange,
  trigger,
  integrations,
  description,
  onClickTrash,
  action,
  actions,
}) => {
  const store = useRootStore();
  const theme = useTheme();
  const buttonRef = useRef();
  const commandsRef = useRef<PillPickerEditorCommands>();
  const [isOpen, setIsOpen] = useState(false);
  const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
  const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
  const [selectedActionMenu, setSelectedActionMenu] = useState<
    DynamicSection | undefined
  >();
  const [pills, setPills] = useState<PillNodeAttrs[]>([]);
  const [tab, setTab] = useState<
    "root" | "trigger-fields" | "action-fields" | "current-item"
  >("root");
  const [loopingMenu, setLoopingMenu] = useState<DynamicSection>();
  const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
    useFieldMissingValidation();
  const [focus, setFocus] = useState<boolean>(false);
  const [actionType, setActionType] = useState<
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined
  >(input.operatorConfig?.operator?.oneofKind);
  const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
  const rootLooping = store.automations.candidate.looping;
  const looping = action
    ? (rootLooping?.scopeForAction?.(action.hash) ?? rootLooping)
    : rootLooping;

  const { valueConfigToTargetPills } = useConverters();

  const filesSaveHandler = (files: SharedFileInput[]): void => {
    addPills(
      files.map((file) => ({
        icon: "",
        title: file.metadata.filename,
        value: JSON.stringify({ target: "file", value: file }),
      })),
    );
  };

  const { openPicker } = useFileUpload(filesSaveHandler, description.editor);

  const addPills = (attrs: PillNodeAttrs[]): void => {
    setTimeout(() => setIsOpen(false));

    const newPills = [...pills, ...attrs];
    setPills(newPills);
    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  const addPill = (attrs: PillNodeAttrs): void => addPills([attrs]);

  const removePill = (_: number, pill: FieldTypePillProps): void => {
    const newPills = pills.filter((item) => item !== pill);
    setPills(newPills);
    onChange({
      ...input,
      value: calculateConfigValue(newPills, actions, trigger),
    });
  };

  useEffect(() => {
    setTriggerMenu(
      convertTriggerToDynamicSection(
        trigger,
        integrations,
        [ValueDescription_T.FILE],
        [ValueDescription_T.FILE],
        ["filefield"],
      ),
    );
  }, [integrations, trigger]);

  // set Looping options for current item
  useEffect(() => {
    if (looping !== undefined) {
      setLoopingMenu(
        convertLoopingSourceOptionsToDynamicSections(
          looping,
          actions,
          integrations,
          [ValueDescription_T.FILE],
          undefined,
          [],
        ),
      );
    }
  }, [actions, action, integrations, looping]);

  useEffect(() => {
    if (action !== undefined) {
      setActionMenus(
        convertActionsToDynamicSections(
          actions,
          action,
          integrations,
          [ValueDescription_T.FILE],
          undefined,
          [],
          looping,
          isSupportedOutputForFile,
        ),
      );
    }
  }, [trigger, action, actions, integrations]);

  useEffect(() => {
    if (actionType === undefined) return;
    if (actionType === OperatorDescriptionKinds.CLEAR) {
      setIsOpen(false);
      setIsCleanAction(true);
    } else {
      setIsCleanAction(false);
    }
    onChange({
      ...input,
      value:
        actionType === OperatorDescriptionKinds.CLEAR
          ? {
              values: [],
            }
          : input.value,
      operatorConfig: {
        // @ts-expect-error
        operator: {
          oneofKind: actionType,
          [actionType]: {},
        },
      },
    });
  }, [actionType, isOpen]);

  useEffect(() => {
    if (triggerFields.length === 0) {
      setTab("root");
    } else {
      setTab("trigger-fields");
    }
  }, [triggerMenu]);

  const validationError =
    action?.actionReference?.actionId === "send-gmail"
      ? checkInputValidation(description, input)
      : false;

  useEffect(() => {
    if (R.isNil(input.value)) return;
    setPills(
      valueConfigToTargetPills(
        input.value,
        trigger,
        actions,
        store.automations.candidate.getTimezone(),
        (label) => label,
        undefined,
        action?.actionReference,
      ),
    );
  }, [input.value, trigger]);

  useEffect(() => {
    if (store.inputToFocus?.inputId === input.inputId) {
      setIsOpen(true);
      store.inputToFocus = undefined;
    }
  }, []);

  const changeActionType = (
    type:
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined,
  ): void => {
    setActionType(type);
  };

  const triggerFields =
    triggerMenu?.options.filter((option) => !option.contextSpecific) ?? [];

  const disabledOptionId = pills.map((pill) => pill.title);

  const buildMenu = (): React.ReactNode => {
    if (validationError) {
      return null;
    }

    return (
      <DropdownPanel
        targetElementRef={buttonRef}
        isActive={isOpen}
        at={POSITION_AT}
        my={POSITION_MY}
        minWidth={300}
        arrowBackground="custom"
        arrowCustomBackground={SolutionColorVariables.sc1}
        panelLineBackground={SolutionColorVariables.sc1}
        portalStyles={getLocalCssColorVariables(theme)}
        onClickOutside={() => setIsOpen(false)}
      >
        {tab === "root" && (
          <MenuOptionWrapper>
            {triggerFields.length > 0 && (
              <MenuOption
                submenuArrow="right"
                onClick={() => setTab("trigger-fields")}
              >
                {t("automation-form.fields-from-trigger")}
              </MenuOption>
            )}
            {actionMenus.map(
              (actionMenu) =>
                actionMenu?.options.length > 0 && (
                  <MenuOption
                    key={actionMenu.hash}
                    submenuArrow="right"
                    onClick={() => {
                      setSelectedActionMenu(actionMenu);
                      setTab("action-fields");
                    }}
                  >
                    {actionMenu.hideNamePrefix === true
                      ? actionMenu.name
                      : t("field-popups.action-popup", {
                          name: actionMenu.name,
                        })}
                  </MenuOption>
                ),
            )}
            {ifLoopingSourceIsActionFind(looping) && (
              <CurrentOption
                addPill={() => {
                  if (ifLoopingSourceIsActionFind(looping)) {
                    setTab(DateTab["current-item"]);
                  }
                }}
              />
            )}
            <MenuOption
              onClick={() => {
                void openPicker();
              }}
              iconName="upload"
              iconColor={SolutionColorVariables.sc1}
              iconSize="M"
              iconAlign="right"
            >
              {t("field-popups.upload-files")}
            </MenuOption>
          </MenuOptionWrapper>
        )}
        {tab === "trigger-fields" && (
          <TriggerOptions
            searchPlaceholder={t("automation-form.search-fields")}
            title={t("automation-form.fields-from-trigger")}
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            options={triggerFields}
            disabledOptionId={disabledOptionId}
          />
        )}
        {tab === "action-fields" && (
          <ActionOptions
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            selectedActionMenu={selectedActionMenu}
            selectedActionHash={selectedActionMenu?.hash}
          />
        )}
        {tab === "current-item" && loopingMenu && (
          <TriggerOptions
            tab={Tab.LOOPING_FIELDS}
            searchPlaceholder={t("automation-form.search-fields")}
            title={loopingMenu.name}
            onNavigateBack={() => setTab("root")}
            addPill={addPill}
            options={loopingMenu.options}
            hasSingleMenuItem={false}
            loopingMenuHash={loopingMenu.hash}
            disabledOptionId={disabledOptionId}
            loopingMenu={loopingMenu}
          />
        )}
      </DropdownPanel>
    );
  };

  const isError =
    !isInputValidByFieldMissing(input, action) || !!validationError;

  const isShowAddButton = !isCleanAction && !validationError;

  const classNames = [
    isCleanAction ? "is-clear" : "",
    validationError ? "pill-picker-error" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Root>
      <PickerStyleWrapper>
        <CaptionBlock description={description} onClickTrash={onClickTrash} />
        <PickerInputField
          showAddButton={isShowAddButton}
          className={classNames}
          state={getEditorState(isOpen || focus, isError)}
          buttonRef={buttonRef}
          // @ts-expect-error
          commandsRef={commandsRef}
          isOpen={isOpen}
          pills={pills}
          pillStyle={{
            textSize: "m",
          }}
          onButtonClick={() => setIsOpen((open) => !open)}
          onPillRemove={removePill}
          placeholder={
            isCleanAction
              ? t("automation-form.clean-operator-editor-placeholder")
              : isOpen
                ? t("automation-form.input-form-select-files")
                : ""
          }
          onClick={() => {
            setIsOpen(true);
          }}
        >
          {action?.actionReference?.actionId === ActionLabel.updateRecord &&
            description?.operators?.operators !== undefined &&
            description?.operators?.operators.length > 0 && (
              <UpdateValueDropdown
                operatorConfig={input.operatorConfig}
                operatorDescriptions={description?.operators.operators}
                onOpenedChange={setFocus}
                onChange={changeActionType}
              />
            )}

          {isOpen && buildMenu()}
        </PickerInputField>
        <FieldMissingError
          message={getFieldMissingErrorMessage(input, action)}
        />
        {validationError && <ErrorMessage message={validationError} />}
      </PickerStyleWrapper>
    </Root>
  );
};

const getEditorState = (
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (isOpen) {
    return "active";
  } else {
    return "default";
  }
};
